import {
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from "@angular/core";
import { AlertController } from "@ionic/angular";
import { TranslateService } from "@ngx-translate/core";
import { Store } from "@ngrx/store";
import * as fromApp from "../../../../../store/app.reducer";
import * as CartCustomOptionsSelectionsActions from "../../store/cart-custom-options-selections/cart-custom-options-selections.actions";

import * as _ from "lodash";
import { auditTime, debounceTime, distinctUntilChanged } from "rxjs/operators";
import { Subscription } from "rxjs";
import { normalizeCartCustomOptionSelection } from "../../cart-custom-options.helpers";

// Popup variant of a text (free-input) cart custom option: same NgRx selection
// flow as app-text-type-choices, but the inputs are presented in an alert popup
// instead of inline items (option.showAsPopup === true). Mirrors the auto-open
// contract of app-select-type-choices so a required option prompts itself when
// "complete order" is pressed.
@Component({
  selector: "app-text-popup-type-choices",
  templateUrl: "./text-popup-type-choices.component.html",
  styleUrls: ["./text-popup-type-choices.component.scss"],
})
export class TextPopupTypeChoicesComponent
  implements OnChanges, OnInit, OnDestroy
{
  @Input() option_timestamp;
  @Input() selectedLang: any;
  public option: any;
  public CustomSelections;
  public finalPriceLoading;
  // Precomputed so the template binds plain values instead of computing them.
  public displayValue = "";
  public headerLabel = "";

  private subscriptions: Subscription[] = [];
  private notValidCustomOptions: any[] = [];
  private completeOrderValidations: any[] = [];
  private pendingAutoOpenValidTs = null;
  private completeOrderLastTs = null;
  private alert: any = null;
  private alertOpen = false;

  constructor(
    private store: Store<fromApp.AppState>,
    private alertController: AlertController,
    private translate: TranslateService
  ) {}

  ngOnChanges(changes: SimpleChanges) {
    // Labels are precomputed, so rebuild them when the language changes.
    if (changes["selectedLang"] && !changes["selectedLang"].firstChange) {
      this.buildLabels();
    }
  }

  ngOnInit() {
    this.subscriptions.push(
      this.store
        .select("cartCustomOptionsSelections")
        .pipe(distinctUntilChanged())
        .subscribe((state) => {
          if (
            state &&
            state.cartCustomOptionsSelections &&
            !_.isEqual(state.cartCustomOptionsSelections, this.CustomSelections)
          ) {
            this.CustomSelections = _.cloneDeep(
              state.cartCustomOptionsSelections
            );
            this.option = _.cloneDeep(
              _.find(this.CustomSelections, {
                timestamp: this.option_timestamp,
              })
            );

            this.buildLabels();
          }
        })
    );

    this.subscriptions.push(
      this.store
        .select("disableFinalPrice")
        .pipe(distinctUntilChanged())
        .pipe(auditTime(100))
        .subscribe((state) => {
          if (
            state &&
            !_.isEqual(state.disableFinalPrice, this.finalPriceLoading)
          ) {
            this.finalPriceLoading = _.cloneDeep(state.disableFinalPrice);
            // An auto-open armed while the price was loading retries here.
            this.maybeAutoOpenPopup();
          }
        })
    );

    // Live list of required-but-unfilled options, produced by the parent
    // validator. Read-only: used only to pick which single popup to auto-open.
    this.subscriptions.push(
      this.store
        .select("cartCustomerOptions")
        .pipe(distinctUntilChanged())
        .subscribe((state) => {
          if (
            state &&
            !_.isEqual(state.notValidCustomOptions, this.notValidCustomOptions)
          ) {
            this.notValidCustomOptions = _.cloneDeep(
              state.notValidCustomOptions || []
            );
          }
        })
    );

    // "Complete order" was pressed: after the validators settle (same 300ms
    // debounce cart-list uses) arm a one-shot auto-open for this required popup.
    this.subscriptions.push(
      this.store
        .select("completeOrderValidation")
        .pipe(debounceTime(300))
        .pipe(distinctUntilChanged())
        .subscribe((state) => {
          if (
            state &&
            state.lastValidTimestamp &&
            !_.isEqual(state.lastValidTimestamp, this.completeOrderLastTs)
          ) {
            this.completeOrderLastTs = _.cloneDeep(state.lastValidTimestamp);
            this.completeOrderValidations = _.cloneDeep(
              state.completeOrderValidations || []
            );
            this.pendingAutoOpenValidTs = state.lastValidTimestamp;
            this.maybeAutoOpenPopup();
          }
        })
    );
  }

  // Header shown at the top of the alert (translated option title) and the
  // summary of what the customer already typed, shown on the trigger item.
  private buildLabels() {
    let header = "";

    if (this.option) {
      header = this.option.title || "";

      if (
        this.option.translations &&
        this.option.translations.title &&
        this.option.translations.title[this.selectedLang]
      ) {
        header = this.option.translations.title[this.selectedLang];
      }
    }

    this.headerLabel = header;
    this.displayValue = this.buildDisplayValue();
  }

  private buildDisplayValue() {
    if (!this.option || !_.isArray(this.option.choices)) {
      return "";
    }

    const typed = _.compact(
      _.map(this.option.choices, (choice) =>
        choice && choice.text ? choice.text.toString().trim() : ""
      )
    );

    return typed.join(", ");
  }

  private buildChoiceLabel(choice) {
    if (!choice) {
      return "";
    }

    if (
      choice.translations &&
      choice.translations.title &&
      choice.translations.title[this.selectedLang]
    ) {
      return choice.translations.title[this.selectedLang];
    }

    return choice.title || "";
  }

  private hasTypedValue() {
    return !!this.buildDisplayValue();
  }

  // When "complete order" is pressed with this required popup still empty, open
  // it automatically. Only the first not-valid popup option opens, so two
  // required popups never stack their alerts.
  private maybeAutoOpenPopup() {
    if (!this.pendingAutoOpenValidTs) {
      return;
    }
    // Time-box the armed press: a stale flag must never open the popup on a
    // later, unrelated notValidCustomOptions recalc.
    if (
      typeof this.pendingAutoOpenValidTs === "number" &&
      Date.now() - this.pendingAutoOpenValidTs > 10000
    ) {
      this.pendingAutoOpenValidTs = null;
      return;
    }
    if (!this.option || !this.option.isRequired) {
      return;
    }
    // Already filled -> nothing to prompt for.
    if (this.hasTypedValue()) {
      this.pendingAutoOpenValidTs = null;
      return;
    }
    // Only surface this popup when it is the LAST thing blocking checkout: if any
    // other validation (delivery method, address, invoice, ...) is still failing,
    // let that error show first. Mirrors cart-list's priority handler.
    const otherValidationFailing = _.some(
      this.completeOrderValidations,
      (v) => v && v.valid === false && v.id !== "cart-custom-options"
    );
    if (otherValidationFailing) {
      this.pendingAutoOpenValidTs = null;
      return;
    }
    const firstNotValidPopup = _.find(
      this.notValidCustomOptions,
      (o) => o && o.showAsPopup
    );
    // Validation may not have populated the list yet; wait for the
    // cartCustomerOptions subscription to fire again.
    if (!firstNotValidPopup) {
      return;
    }
    if (firstNotValidPopup.timestamp !== this.option_timestamp) {
      this.pendingAutoOpenValidTs = null;
      return;
    }
    // The trigger is disabled while the price loads; keep the flag armed and
    // retry from the disableFinalPrice subscription once it settles.
    if (this.finalPriceLoading) {
      return;
    }
    this.pendingAutoOpenValidTs = null;
    this.openPopup();
  }

  public async openPopup() {
    if (
      !this.option ||
      !_.isArray(this.option.choices) ||
      this.option.choices.length === 0 ||
      this.finalPriceLoading ||
      this.alertOpen
    ) {
      return;
    }

    // Claimed synchronously: creating the alert is async, so an auto-open and a
    // tap (or a double tap) would otherwise both get past this guard and stack
    // two alerts.
    this.alertOpen = true;

    const numericOnly = !!this.option.numericOnly;

    const inputs: any[] = _.map(this.option.choices, (choice) => ({
      name: this.choiceKey(choice),
      type: numericOnly ? "number" : "text",
      // Brings up the numeric keypad on mobile. The value is sanitised on
      // confirm anyway, because a number input still lets some browsers through
      // with "e", "+" or "-".
      attributes: numericOnly ? { inputmode: "numeric" } : {},
      value: choice.text || "",
      placeholder:
        this.buildChoiceLabel(choice) || this.translate.instant("write-here"),
    }));

    try {
      this.alert = await this.alertController.create({
        header: this.headerLabel,
        cssClass: "textPopupTypeChoices",
        backdropDismiss: true,
        inputs: inputs,
        buttons: [
          {
            text: this.translate.instant("cancel"),
            role: "cancel",
            cssClass: "secondary",
          },
          {
            text: this.translate.instant("save"),
            handler: (data) => {
              // The option can disappear from the selections while the alert is
              // open (a cart clear). Throwing in here would keep the alert from
              // dismissing and wedge it for good, so just let it close.
              if (!this.option) {
                return true;
              }

              const values = this.collectValues(data);

              // A required option must not be confirmed empty. Returning false
              // keeps the alert open; cancel/backdrop still lets the customer
              // out and the parent validator keeps checkout blocked.
              if (this.option.isRequired && _.isEmpty(values)) {
                return false;
              }

              this.applyValues(values);
              return true;
            },
          },
        ],
      });

      await this.alert.present();
      await this.alert.onDidDismiss();
    } finally {
      this.alert = null;
      this.alertOpen = false;
    }
  }

  private choiceKey(choice) {
    return choice && choice.timestamp != null
      ? choice.timestamp.toString()
      : "";
  }

  // Alert inputs are keyed by choice timestamp. Keep only the trimmed,
  // non-empty values so an all-whitespace answer never counts as filled. On a
  // numeric option everything but the digits is dropped, so a value that is all
  // punctuation reads as empty and a required option stays open.
  private collectValues(data) {
    const values: any = {};
    const numericOnly = !!(this.option && this.option.numericOnly);

    _.each(this.option && this.option.choices, (choice) => {
      const key = this.choiceKey(choice);
      let raw = key && data && data[key] ? data[key].toString().trim() : "";

      if (numericOnly) {
        raw = raw.replace(/\D/g, "");
      }

      if (raw) {
        values[key] = raw;
      }
    });

    return values;
  }

  private applyValues(values) {
    if (!this.option || !_.isArray(this.option.choices)) {
      return;
    }

    const updatedOption = _.cloneDeep(this.option);

    _.each(updatedOption.choices, (choice) => {
      const value = values[this.choiceKey(choice)];

      if (value) {
        choice.text = value;
        choice.selected = true;
      } else {
        choice.text = "";
        choice.selected = false;
      }
    });

    this.store.dispatch(
      new CartCustomOptionsSelectionsActions.AddUpdateCartCustomOptionSelections(
        normalizeCartCustomOptionSelection(updatedOption)
      )
    );
  }

  ngOnDestroy() {
    if (this.alert) {
      // The component is going away; nothing is left to handle a rejection.
      this.alert.dismiss().catch(() => {});
      this.alert = null;
      this.alertOpen = false;
    }

    if (this.subscriptions && this.subscriptions.length > 0) {
      this.subscriptions.forEach((sub) => {
        sub.unsubscribe();
      });
    }
  }
}
